import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { loadFilePond } from '../models/filepond_loader'

let uploadProcess = {}
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
export default class MediaController extends Controller {
  static targets = ['multiple', 'single', 'profileimage', 'galleryfolder', 'default']
  static values = {
    user: Object,
    documents: Object,
    img: Object
  }

  connect() {
    this.handlePageShow = this.restoreCachedPond.bind(this)
    globalThis.addEventListener('pageshow', this.handlePageShow)

    if (this.restoreCachedPond()) return

    if (this.hasMultipleTarget) {
      this.ensureMultiplePond()
    }
  }

  restoreCachedPond() {
    const form = this.element.closest('form')
    if (form?.dataset.filePondCacheSuspended !== 'true') return false

    delete form.dataset.filePondCacheSuspended

    requestAnimationFrame(() => {
      if (this.hasMultipleTarget) this.ensureMultiplePond()
    })

    return true
  }

  async getFilePond() {
    return this.FilePond || (this.FilePond = await loadFilePond())
  }

  async defaultTargetConnected() {
    const FilePond = await this.getFilePond()
    let params = this.documentsValue
    const element = this.defaultTarget
    this.directUploadDefault = Boolean(element.dataset.directUploadUrl)
    const options = {
      storeAsFile: !this.directUploadDefault,
      server: this.directUploadDefault ? this.directUploadServer(element) : undefined,
      // className: 'h-25',
      // height: '50px',
      imagePreviewHeight: 180,
      credits: false,
      itemInsertLocation: 'bottom',
      files: params.files,
      onaddfile: (e, fileItem) => {
        if (this.checkDuplicateFile(this.pond, fileItem)) return
        this.isValid()
      },

      onwarning: (warning, file) => {
        if (warning.body === 'Max files') {
          alert(`You can upload a maximum of ${element.dataset.maxFiles} images.`)
        }
      },

      onerror: (error, fileObj) => {
        if (error) {
          alert(`${error.main}. ${error.sub}.`)
          pond.removeFile(fileObj?.id)
        }
      },

      beforeRemoveFile: async (item) => {
        const result = await this.showAlert(
          'Confirmation',
          'Are you sure you want to remove this file?',
          'warning'
        )

        if (result.isConfirmed) {
          this.isValid()
          return true
        }

        return false
      }
    }

    if (element.dataset.maxFiles) {
      options.maxFiles = parseInt(element.dataset.maxFiles, 10)
    }

    if (element.dataset.maxFileSize) {
      options.maxFileSize = element.dataset.maxFileSize
      options.allowFileSizeValidation = true
    }

    this.directUploadDefault = Boolean(element.dataset.directUploadUrl)
    const pond = FilePond.create(element, options)
    this.pond = pond

    if (this.directUploadDefault) {
      this.pond.on('processfilestart', () => this.isValid())
      this.pond.on('processfile', () => this.isValid())
      this.pond.on('processfileabort', () => this.isValid())
      this.pond.on('processfileerror', () => this.isValid())
      this.directUploadForm = this.element.closest('form')
      this.directUploadSubmitHandler = (event) => {
        if (this.pond.getFiles().length === 0 || this.pond.status === FilePond.Status.READY) return

        event.preventDefault()
        alert('Please wait for all files to finish uploading.')
      }
      this.directUploadForm?.addEventListener('submit', this.directUploadSubmitHandler)
    }
    this.enableImageExpand(this.pond)
  }

  async singleTargetConnected() {
    const FilePond = await this.getFilePond()
    let params = this.userValue
    let width = ''
    let height = 100

    if (params.file_type === 'nft_thumbnail') {
      width = 294
      height = 260
    } else if (params.file_type === 'user_thumbnail') {
      width = 500
      height = 500
    }

    let options = {
      labelIdle:
        params.file_type === 'nft_thumbnail'
          ? '<p class="bg-clip-text text-transparent text-gradient-orange fs-28">Upload thumbnail to preview NFT</p>'
          : '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
          '                          <path d="M27.625 0.5H4.375C2.23886 0.5 0.5 2.23886 0.5 4.375V27.625C0.5 29.7611 2.23886 31.5 4.375 31.5H27.625C29.7611 31.5 31.5 29.7611 31.5 27.625V4.375C31.5 2.23886 29.7611 0.5 27.625 0.5ZM4.375 3.75H27.625C27.9684 3.75 28.25 4.03164 28.25 4.375V17.6819L23.3166 12.7484C23.0139 12.4457 22.6021 12.272 22.168 12.272H22.1635C21.732 12.272 21.3125 12.4441 21.0077 12.7588C21.007 12.7594 21.0064 12.7601 21.0058 12.7607L14.8866 18.9749L12.5241 16.6214C12.524 16.6214 12.5239 16.6213 12.5239 16.6212C12.2297 16.3272 11.8326 16.1549 11.4122 16.1454C10.9113 16.0855 10.4726 16.3671 10.2135 16.6379L3.75 23.2378V4.375C3.75 4.03164 4.03164 3.75 4.375 3.75ZM4.375 28.25C4.11176 28.25 3.88251 28.0792 3.79536 27.8369L11.3906 20.0791L19.5981 28.25H4.375ZM27.625 28.25H24.2015L17.1896 21.2661L22.1752 16.2043L28.25 22.2776V27.625C28.25 27.9684 27.9684 28.25 27.625 28.25Z" fill="white" stroke="#5452AD"/>\n' +
          '                          <path d="M11.302 13.2765C12.8556 13.2765 14.115 12.0171 14.115 10.4635C14.115 8.90992 12.8556 7.6505 11.302 7.6505C9.74844 7.6505 8.48901 8.90992 8.48901 10.4635C8.48901 12.0171 9.74844 13.2765 11.302 13.2765Z" fill="white" stroke="#5452AD"/>\n' +
          '                        </svg> ',
      storeAsFile: true,
      imagePreviewWidth: width,
      labelTapToCancel: '',
      imagePreviewHeight: height,
      filePosterHeight: height,
      filePosterMaxHeight: height
    }
    const pond = FilePond.create(this.element, {
      onaddfile: (error, fileItem) => {
        this.checkDuplicateFile(pond, fileItem)
      },
      beforeRemoveFile: (file) => {
        return Swal.fire({
          customClass: {
            popup:
              'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
            htmlContainer: 'text-black-paint text-white'
          },
          animation: false,
          title: 'Do you want to delete this file?',
          showCancelButton: true,
          confirmButtonText: 'Delete',
          itemInsertLocation: 'bottom',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#d56859',
          showLoaderOnConfirm: true
        }).then((result) => {
          if (result.isConfirmed) {
            pond.removeFile(file)
            if (params.file_type != 'nft_thumbnail') {
              this.delete_data(params.remove_url, {
                img_id: file.file['id'],
                image_column: file.file['column_name']
              })
            }
          }
        })
      }
    })
    this.enableImageExpand(pond)
    if (params.file_type == 'nft_thumbnail') {
      pond.on('addfile', () => {
        if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
          document.getElementById('nft_image').scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
        const errInput = document.querySelector('.filepond--browser.error')
        if (errInput) errInput.classList.remove('error')
        const err = document.querySelector('.required-error label.error')
        if (err) err.remove()
      })
    }
    if (params.name != '') {
      options['files'] = [
        {
          source: params?.source,
          options: {
            type: 'local',
            file: {
              name: params.name,
              size: params.size,
              type: params.type,
              id: params.id,
              column_name: params.column_name
            },
            metadata: {
              poster: params.url
            }
          }
        }
      ]
    }
    pond.setOptions(options)
  }

  async profileimageTargetConnected() {
    const FilePond = await this.getFilePond()
    let params = this.userValue
    let options = {
      labelIdle:
        params.file_type === 'user_profile'
          ? '<svg width="50" height="50" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
          '<path d="M27.625 0.5H4.375C2.23886 0.5 0.5 2.23886 0.5 4.375V27.625C0.5 29.7611 2.23886 31.5 4.375 31.5H27.625C29.7611 31.5 31.5 29.7611 31.5 27.625V4.375C31.5 2.23886 29.7611 0.5 27.625 0.5ZM4.375 3.75H27.625C27.9684 3.75 28.25 4.03164 28.25 4.375V17.6819L23.3166 12.7484C23.0139 12.4457 22.6021 12.272 22.168 12.272H22.1635C21.732 12.272 21.3125 12.4441 21.0077 12.7588C21.007 12.7594 21.0064 12.7601 21.0058 12.7607L14.8866 18.9749L12.5241 16.6214C12.524 16.6214 12.5239 16.6213 12.5239 16.6212C12.2297 16.3272 11.8326 16.1549 11.4122 16.1454C10.9113 16.0855 10.4726 16.3671 10.2135 16.6379L3.75 23.2378V4.375C3.75 4.03164 4.03164 3.75 4.375 3.75ZM4.375 28.25C4.11176 28.25 3.88251 28.0792 3.79536 27.8369L11.3906 20.0791L19.5981 28.25H4.375ZM27.625 28.25H24.2015L17.1896 21.2661L22.1752 16.2043L28.25 22.2776V27.625C28.25 27.9684 27.9684 28.25 27.625 28.25Z" fill="white" stroke="#5452AD"/>\n' +
          '<path d="M11.302 13.2765C12.8556 13.2765 14.115 12.0171 14.115 10.4635C14.115 8.90992 12.8556 7.6505 11.302 7.6505C9.74844 7.6505 8.48901 8.90992 8.48901 10.4635C8.48901 12.0171 9.74844 13.2765 11.302 13.2765Z" fill="white" stroke="#5452AD"/>\n' +
          '</svg>'
          : ' <span class="btn button-md bg-image-btn bg-blue-img-btn scale-hover mb-4 py-4 mt-n5 ms-auto">\n' +
          '  <span class="text-black d-block p-font-bold">UPLOAD FROM COMPUTER</span>\n' +
          '  </span>',
      storeAsFile: true,
      imagePreviewHeight: 100,
      // imageCropAspectRatio: '1:1',
      imageResizeTargetWidth: 100,
      itemInsertLocation: 'bottom',
      imageResizeTargetHeight: 100,
      labelTapToCancel: '',
      stylePanelLayout:
        params.file_type === 'user_profile' ? 'compact circle' : '',
      styleLoadIndicatorPosition: 'center bottom',
      styleButtonRemoveItemPosition:
        params.file_type === 'user_profile' ? 'center bottom' : ''
    }
    const pond = FilePond.create(this.element, {
      beforeRemoveFile: this.createBeforeRemoveHandler(params)
    })
    this.enableImageExpand(pond)
    if (params.name != '') {
      options['files'] = [
        {
          options: {
            type: 'local',
            file: {
              name: params.name,
              size: params.size,
              type: params.type,
              column_name: params.column_name
            },
            metadata: {
              poster: params.url
            }
          }
        }
      ]
    }
    pond.setOptions(options)
  }

  async multipleTargetConnected() {
    return this.ensureMultiplePond()
  }

  async ensureMultiplePond() {
    if (
      this.element.closest('form')
        ?.dataset.filePondCacheSuspended === 'true'
    ) {
      return null
    }

    if (this.multiplePondInitialization) {
      return this.multiplePondInitialization
    }

    this.multiplePondInitialization = this.initializeMultiplePond()

    try {
      return await this.multiplePondInitialization
    } finally {
      this.multiplePondInitialization = null
    }
  }

  async initializeMultiplePond() {
    const FilePond = await this.getFilePond()
    let params = this.documentsValue
    let element = this.multipleTarget

    const existingPond = FilePond.find(element)
    if (existingPond) {
      this.pond = existingPond
      return existingPond
    }

    this.pond?.destroy()
    this.pond = null

    this.multipleInput = element
    this.submitStateScope = element.closest(
      '[data-controller*="submit-state"]'
    )

    this.directUploadMultiple = Boolean(element.dataset.directUploadUrl)
    let options = {
      storeAsFile: !this.directUploadMultiple,
      server: this.directUploadMultiple ? this.directUploadServer(element) : undefined,
      // filePosterHeight: 100,
      // mediaPreviewHeight: 100,
      // filePosterMaxHeight: 100,
      // imagePreviewHeight: 100,
      labelTapToCancel: '',
      imageResizeTargetWidth: 200,
      itemInsertLocation: 'bottom',
      // imageResizeTargetHeight: 200,
      allowFileSizeValidation: true,
      maxFileSize: '50MB',
      maxFiles: 10,
      imagePreviewHeight: 180,
      credits: false
    }
    const pond = FilePond.create(element, {
      beforeRemoveFile: this.createBeforeRemoveHandler(params),
      onerror: (error, fileObj) => {
        if (error) {
          alert(`${error.main}. ${error.sub}.`)
          pond.removeFile(fileObj?.id);
        }
      },
    })

    this.pond = pond
    if (this.directUploadMultiple) {
      this.activePostModalUploads = new Set()
      pond.on('addfilestart', (file) => {
        this.trackPostModalUpload(file)
      })
      pond.on('addfile', (error, file) => {
        if (error) this.finishPostModalUpload(file)
      })
      pond.on('processfilestart', (file) => {
        this.trackPostModalUpload(file)
        this.isValid()
      })
      pond.on('processfiles', () => {
        this.activePostModalUploads.clear()
        this.updatePostModalUploadState()
        this.isValid()
      })
      pond.on('processfile', (_error, file) => this.finishPostModalUpload(file))
      pond.on('processfileabort', (file) => this.finishPostModalUpload(file))
      pond.on('processfileerror', (_error, file) => this.finishPostModalUpload(file))
      pond.on('removefile', (_error, file) => this.finishPostModalUpload(file))
      this.directUploadForm = this.element.closest('form')
      this.directUploadSubmitHandler = (event) => {
        if (pond.getFiles().length === 0 || pond.status === FilePond.Status.READY) return

        event.preventDefault()
        alert('Please wait for all files to finish uploading.')
      }
      this.directUploadForm?.addEventListener('submit', this.directUploadSubmitHandler)
    }
    this.enableImageExpand(pond)
    if (params.files !== '') {
      options['files'] = params.files
      pond.on(
        'removefile',
        function (error, file) {
          let id = file.file['id']
          let img_id = file.file['img_id']
          let image_column = file.file['image_column']
          if (params.remove_url) {
            this.delete_data(params.remove_url, {
              img_id: img_id,
              id: id,
              image_column: image_column
            })
          }
          this.toggleVisibility()
          this.dispatchMediaStateUpdate()
          this.isValid()
        }.bind(this)
      )
      pond.on(
        'addfile',
        function (error, fileItem) {
          this.toggleVisibility()
          this.dispatchMediaStateUpdate()
          this.isValid()
          this.checkDuplicateFile(pond, fileItem)
        }.bind(this)

      )
      pond.on('warning', (warning, file) => {
        if (warning.body === "Max files") {
          alert("You can upload a maximum of 10 files.");
        }
      });
    }
    pond.setOptions(options)
    this.toggleVisibility()

    return pond
  }

  directUploadServer(element) {
    return {
      process: (_fieldName, file, _metadata, load, error, progress, abort) => {
        let request
        const upload = new DirectUpload(file, element.dataset.directUploadUrl, {
          directUploadWillStoreFileWithXHR: (xhr) => {
            request = xhr
            xhr.upload.addEventListener('progress', (event) => {
              progress(event.lengthComputable, event.loaded, event.total)
            })
          }
        })

        upload.create((uploadError, blob) => {
          if (uploadError) error('Upload failed. Please try again.')
          else load(blob.signed_id)
        })

        return {
          abort: () => {
            request?.abort()
            abort()
          }
        }
      },
      revert: (_signedId, load) => load()
    }
  }

  toggleVisibility() {
    if (!this.pond) return
    if (globalThis.location.pathname.includes('/libraries')) return;

    const root = this.pond.element
    if (this.pond.getFiles().length > 0 || this.multipleInput?.dataset?.labelValue) {
      root.classList.remove('d-none')
    } else {
      root.classList.add('d-none')
    }
  }

  async galleryfolderTargetConnected(e) {
    const FilePond = await this.getFilePond()
    let params = this.documentsValue
    let image = this.galleryfolderTarget.dataset?.imgValue
    const uploadLabel = this.galleryfolderTarget.dataset?.uploadLabel || 'Upload Media'
    this.directUploadGalleryVideos = Boolean(
      this.galleryfolderTarget.dataset.directUploadUrl
    )
    this.pond = FilePond.create(this.element, {
      labelIdle:
        '<span class="d-inline-flex flex-column align-items-center w-100">' +
        '<span class="d-block w-100 overflow-hidden mb-3">' +
        '<img class="d-block img-fluid mx-auto" width="340" src=\'' + image + "' alt=''/>" +
        '</span>' +
        '<span class="bg-image-btn mx-auto h-auto mb-0 py-24 d-flex align-items-center justify-content-center">' + uploadLabel + '</span>' +
        '</span>',
      // imagePreviewHeight: 100,
      // filePosterHeight: 100,
      // labelTapToCancel: '',
      // filePosterMaxHeight: 100,
      storeAsFile: !this.directUploadGalleryVideos,
      server: this.directUploadGalleryVideos ? this.galleryDirectUploadServer() : undefined,
      itemInsertLocation: 'bottom',
      files: params.files,
      maxFileSize: '50MB',
      imagePreviewHeight: 180,
      credits: false,
      onaddfile: (e, fileItem) => {
        if (this.checkDuplicateFile(this.pond, fileItem)) return
        this.isValid()
      },
      beforeRemoveFile: this.createBeforeRemoveHandler(params)
    })
    if (this.directUploadGalleryVideos) {
      this.pond.on('processfilestart', () => this.isValid())
      this.pond.on('processfile', () => this.isValid())
      this.pond.on('processfileabort', () => this.isValid())
      this.pond.on('processfileerror', () => this.isValid())
      this.pond.on('removefile', () => this.isValid())
    }
    this.enableImageExpand(this.pond)
  }

  galleryDirectUploadServer() {
    return {
      process: (_fieldName, file, _metadata, load, error, progress, abort) => {
        let request
        const upload = new DirectUpload(file, this.galleryfolderTarget.dataset.directUploadUrl, {
          directUploadWillStoreFileWithXHR: (xhr) => {
            request = xhr
            xhr.upload.addEventListener('progress', (event) => {
              progress(event.lengthComputable, event.loaded, event.total)
            })
          }
        })

        upload.create((uploadError, blob) => {
          if (uploadError) {
            error('Video upload failed. Please try again.')
          } else {
            load(blob.signed_id)
          }
        })

        return {
          abort: () => {
            request?.abort()
            abort()
          }
        }
      },
      // Unattached blobs are removed later by Active Storage's cleanup job.
      revert: (_signedId, load) => load()
    }
  }

  enableImageExpand(pond) {
    const refresh = () => globalThis.requestAnimationFrame(() => {
      globalThis.requestAnimationFrame(() => {
        this.renderImageExpandButtons(pond)
        this.prepareVideoPreviews(pond)
      })
    })

    pond.on('addfile', refresh)
    pond.on('removefile', refresh)
    pond.on('reorderfiles', refresh)

    this.imageExpandObserver?.disconnect()
    this.imageExpandObserver = new globalThis.MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) refresh()
    })
    this.imageExpandObserver.observe(pond.element, {
      childList: true,
      subtree: true
    })
    refresh()
  }

  prepareVideoPreviews(pond) {
    const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

    if (!isIOS) return

    pond.element
      .querySelectorAll('.filepond--media-preview video')
      .forEach((video) => {
        if (video.dataset.iosPreviewPrepared === 'true') return

        video.dataset.iosPreviewPrepared = 'true'
        video.muted = true
        video.preload = 'metadata'
        video.playsInline = true
        video.setAttribute('playsinline', '')
        video.setAttribute('webkit-playsinline', '')

        const revealFirstFrame = () => {
          if (!Number.isFinite(video.duration) || video.duration <= 0) return

          try {
            // iOS Safari often leaves a local video transparent at time zero
            // until playback. A tiny seek forces it to decode and paint a frame.
            video.currentTime = Math.min(0.1, video.duration / 10)
          } catch (error) {
            console.warn('Unable to prepare the selected video preview:', error)
          }
        }

        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
          revealFirstFrame()
        } else {
          video.addEventListener('loadedmetadata', revealFirstFrame, { once: true })
        }

        video.load()
      })
  }

  disconnect() {
    globalThis.removeEventListener('pageshow', this.handlePageShow)
    this.imageExpandObserver?.disconnect()
    this.directUploadForm?.removeEventListener('submit', this.directUploadSubmitHandler)
  }

  renderImageExpandButtons(pond) {
    const items = pond.getFiles()
    const itemsById = new Map(items.map((item) => [item.id, item]))
    const itemElements = pond.element.querySelectorAll('.filepond--item')
    const listIsStable = itemElements.length === items.length

    itemElements.forEach((itemElement, index) => {
      let itemId = itemElement.dataset.filepondExpandId

      if (!itemId && listIsStable) {
        itemId = items[index]?.id
        if (itemId) itemElement.dataset.filepondExpandId = itemId
      }

      const fileItem = itemsById.get(itemId)
      const existingButton = itemElement.querySelector('.filepond--image-expand')

      // During removal FilePond temporarily has more DOM elements than files.
      // Keep existing element-to-file bindings until its list is stable again.
      if (!fileItem) return

      if (!this.isImageFile(fileItem)) {
        existingButton?.remove()
        return
      }

      if (existingButton) {
        existingButton.dataset.src = fileItem.getMetadata?.('poster') || ''
        existingButton.fancyboxFile = fileItem.file
        this.bindImagePreviewExpand(itemElement)
        return
      }

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'filepond--image-expand'
      button.title = 'Expand image'
      button.dataset.controller = 'fancybox'
      button.dataset.action = 'click->fancybox#open'
      button.dataset.src = fileItem.getMetadata?.('poster') || ''
      button.fancyboxFile = fileItem.file
      button.setAttribute('aria-label', 'Expand image')
      button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#fff" viewBox="0 0 256 256"><path d="M216,48V96a8,8,0,0,1-16,0V67.31l-42.34,42.35a8,8,0,0,1-11.32-11.32L188.69,56H160a8,8,0,0,1,0-16h48A8,8,0,0,1,216,48ZM98.34,146.34,56,188.69V160a8,8,0,0,0-16,0v48a8,8,0,0,0,8,8H96a8,8,0,0,0,0-16H67.31l42.35-42.34a8,8,0,0,0-11.32-11.32ZM208,152a8,8,0,0,0-8,8v28.69l-42.34-42.35a8,8,0,0,0-11.32,11.32L188.69,200H160a8,8,0,0,0,0,16h48a8,8,0,0,0,8-8V160A8,8,0,0,0,208,152ZM67.31,56H96a8,8,0,0,0,0-16H48a8,8,0,0,0-8,8V96a8,8,0,0,0,16,0V67.31l42.34,42.35a8,8,0,0,0,11.32-11.32Z"/></svg>'
      itemElement.append(button)
      this.bindImagePreviewExpand(itemElement)
    })
  }

  bindImagePreviewExpand(itemElement) {
    if (itemElement.dataset.imagePreviewExpandBound === 'true') return

    itemElement.dataset.imagePreviewExpandBound = 'true'
    itemElement.addEventListener('click', (event) => {
      const preview = event.target.closest(
        '.filepond--image-preview, .filepond--image-preview-wrapper, .filepond--image-preview-overlay'
      )
      if (!preview) return

      itemElement.querySelector('.filepond--image-expand')?.click()
    })
  }

  isImageFile(fileItem) {
    const type = fileItem?.file?.type || ''
    const name = fileItem?.file?.name || ''

    return type.startsWith('image/') || /\.(avif|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(name)
  }

  delete_data(remove_url, item_data) {
    let id = item_data['img_id']
    if (id) {
      $.ajax({
        url: remove_url,
        type: 'POST',
        datatype: 'Script',
        headers: {
          'X-CSRF-Token': csrfToken
        },
        data: {
          column_names: item_data['image_column'],
          id: item_data['img_id']
        },
        success: function (result) { }
      })
    }
  }

  isValid() {
    const $form = $(this?.pond?.element?.firstChild)?.closest('form')
    const uploadsReady = (!this.directUploadGalleryVideos && !this.directUploadMultiple) || this.pond.status === this.FilePond.Status.READY

    if ($form?.valid() && uploadsReady) {
      $form.find("[type='submit']").attr('disabled', false);
    } else {
      $form.find("[type='submit']").attr('disabled', true)
    }
  }

  trackPostModalUpload(file) {
    if (!file || file.origin !== this.FilePond.FileOrigin.INPUT) return

    this.activePostModalUploads.add(file.id)
    this.updatePostModalUploadState()
  }

  finishPostModalUpload(file) {
    if (file) this.activePostModalUploads.delete(file.id)

    this.updatePostModalUploadState()
    this.isValid()
  }

  updatePostModalUploadState() {
    this.setPostModalDisabled(this.activePostModalUploads.size > 0)
  }

  setPostModalDisabled(disabled) {
    const postModal = this.element.closest('form')?.querySelector('#post_modal')
    if (!postModal) return

    postModal.classList.toggle('disabled', disabled)
    postModal.setAttribute('aria-disabled', disabled.toString())
    if (disabled) {
      postModal.setAttribute('tabindex', '-1')
    } else {
      postModal.removeAttribute('tabindex')
    }
  }

  createBeforeRemoveHandler(params = {}, pond = null) {
    return async (file) => {
      const confirmed = await confirmation()

      if (!confirmed) {
        return false
      }

      this.isValid()

      if (
        params.remove_url &&
        file?.file?.id &&
        params.file_type !== 'nft_thumbnail'
      ) {
        this.delete_data(params.remove_url, {
          img_id: file.file.id,
          image_column: file.file.column_name
        })
      }

      return true
    }
  }

  showAlert(title, message, icon) {
    return Swal.fire({
      customClass: {
        popup:
          'swal-popup alert-sm-bg align-items-center justify-content-center w-100',
        htmlContainer: 'text-black-paint text-white'
      },
      animation: false,
      title: title,
      text: message,
      icon: icon,
      confirmButtonText: 'OK',
      showCancelButton: true,
      cancelButtonText: 'Cancel'
    })
  }

  getSubmitStateScope() {
    if (this.submitStateScope) return this.submitStateScope

    return this.element.closest('[data-controller*="submit-state"]')
  }

  dispatchMediaStateUpdate() {
    const scope = this.getSubmitStateScope()
    const fileCount = this.pond?.getFiles?.().length || 0

    if (!scope) return

    document.dispatchEvent(
      new CustomEvent('form:media-changed', {
        detail: {
          scope,
          hasMedia: fileCount > 0,
          mediaCount: fileCount
        }
      })
    )
  }

  checkDuplicateFile(pond, fileItem) {
    const duplicate = pond.getFiles().some((existing) => {
      if (existing.id === fileItem.id) return false

      return (
        existing.file.name === fileItem.file.name &&
        existing.file.size === fileItem.file.size &&
        existing.file.lastModified === fileItem.file.lastModified
      )
    })

    if (duplicate) {
      pond.removeFile(fileItem.id)
      alert('This file has already been added.')
      return true
    }

    return false
  }
}
